import { AMPLITUDE_EVENTS } from '@/constants/analytics';
import { PAY_MODE } from '@/constants/order';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import useUser from '@/mp/hooks/useUser';
import {
  assignPayment,
  createDiscountCode,
  setSelectedPayMode,
} from '@/redux/actions';
import React, { useEffect, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useWizard } from 'react-use-wizard';
import routes from '@/routes/constants';
import { amplitudeTrack } from '@/utilities';
import { usePaymentContext } from '@/mp/hooks/usePaymentContext';
import text from '@/text';
import {
  useCreatePaymentMethodSetupMutation,
  useGetJobQuery,
  useUpdateOrderProgressMutation,
} from '@/redux/rtk';
import { Formik } from 'formik';
import StripeElements from '@/components/stripe/StripeElements';
import PaymentMethodForm from './PaymentMethodForm';
import Step from '@/components/wizard/Step';
import Loading from '@/components/loading/Loading';
import StepFooter from '../StepFooter';
import ToggleButtonGroup from '@/components/form/ToggleButtonGroup';
import { isJobPayableOrQuoted } from '@/utilities/job';
import { Button } from '@/components/button';
import AssignedPaymentForm from '@/components/form/assignedPaymentForm/AssignedPaymentForm';
import { assignedPaymentSchema } from '@/validation';
import { useLeads } from '@/mp/hooks/useLeads';
import { orderV2Selectors } from '@/redux/selectors/order.v2';
import { isRouteIncluded } from '@/utilities/routes';
import { getStepNameByIndex } from '../../../../pages/billing/OrderWizard/orderStepsFactory';
import useRedirectToMap from '../../../hooks/useRedirectToMap';
import { useCommitQuote } from '@/mp/hooks/useCommitQuote';
import { shallowEqual } from 'react-redux';

const { PAYMENT_FLOW } = AMPLITUDE_EVENTS;

const PaymentMethodStep = ({ nextLabel }: { nextLabel?: string }) => {
  // const history = useHistory();
  // const dispatch = useDispatch();
  // const { nextStep } = useWizard();
  // const { jobId } = useParams();
  // const isBreakpointEnabled = useBreakpoint(['xs', 'sm']);
  const { jobId, asMember } = usePaymentContext();
  const currentUser = useUser();
  const { selectedPayMode, membershipPriceId, paymentMethodId, discountCode } =
    useAppSelector(
      (state) => ({
        selectedPayMode: state.order.selectedPayMode,
        membershipPriceId: state.order.membershipPriceId,
        paymentMethodId: state.order.paymentMethodId,
        discountCode: state.order.discountCode,
      }),
      shallowEqual
    );

  const dispatch = useAppDispatch();
  const history = useHistory();
  const { nextStep, activeStep } = useWizard();

  const [createPaymentMethodSetup, createPaymentMethodSetupState] =
    useCreatePaymentMethodSetupMutation();
  const { data: job } = useGetJobQuery(jobId);
  const { sendPaymentPageLead } = useLeads();
  const [updateOrderProgress] = useUpdateOrderProgressMutation();

  const quote = useAppSelector((state) =>
    orderV2Selectors.getQuote(state, jobId, asMember)
  );
  const customerSoftware = useAppSelector(orderV2Selectors.getCustomerSoftware);
  const comments = useAppSelector(orderV2Selectors.getComments);

  const { redirectToMap } = useRedirectToMap();

  const isAssignedPayment = selectedPayMode === PAY_MODE.ASSIGNED_PAYMENT;

  const availablePayModes = useMemo(
    () => [
      { label: 'myPaymentDetails', mode: PAY_MODE.PAYMENT_METHOD },
      {
        label: 'sendToPayer',
        mode: PAY_MODE.ASSIGNED_PAYMENT,
        isEnabled: () => isJobPayableOrQuoted(job),
      },
    ],
    [job]
  );

  const location = useLocation();
  const isPaymentFlow = isRouteIncluded([routes.mp.payment(jobId)])(
    location.pathname
  );

  useCommitQuote();

  const handleSubmit = async (jobIdToAssign: number, values: any) => {
    if (isAssignedPayment && values) {
      const success = await dispatch(
        assignPayment(
          membershipPriceId,
          jobIdToAssign,
          values.payerEmail,
          values.payerName,
          values.message
        )
      );
      if (success) {
        history.push({
          pathname: routes.payment.assignedStatus(),
          state: { payerEmail: values.payerEmail, payerName: values.payerName },
        });
      }
    } else {
      if (discountCode) {
        const success = await dispatch(createDiscountCode(discountCode));
        if (!success) {
          return;
        }
      }
      amplitudeTrack(PAYMENT_FLOW.EVENT, {
        action: PAYMENT_FLOW.CONTINUE_TO_CONFIRMATION,
      });
      nextStep();
    }
  };

  const hasSentLeadRef = useRef(false);
  useEffect(() => {
    if (!quote) return;
    if (!customerSoftware) return;
    if (hasSentLeadRef.current) return;

    hasSentLeadRef.current = true;
    sendPaymentPageLead({
      jobId,
      data: { quote, architecturalSoftware: customerSoftware, comments },
    });
  }, [quote, customerSoftware, comments, jobId, sendPaymentPageLead]);

  useEffect(() => {
    if (!isPaymentFlow || !jobId) return;
    const orderProgress = getStepNameByIndex(
      activeStep,
      currentUser.isEssentialUser || currentUser.isGovernment
    );
    if (!orderProgress) return;
    updateOrderProgress({
      jobId,
      orderProgress,
    });
  }, [
    activeStep,
    currentUser.isEssentialUser,
    currentUser.isGovernment,
    isPaymentFlow,
    jobId,
    updateOrderProgress,
  ]);

  // @ts-expect-error event
  const handleSelectedPayMode = (event) =>
    dispatch(setSelectedPayMode(event.currentTarget.value));

  useEffect(() => {
    createPaymentMethodSetup();
  }, [createPaymentMethodSetup]);

  if (createPaymentMethodSetupState.isLoading) {
    return <Loading />;
  }

  return (
    <Formik
      validateOnMount
      validateOnChange
      initialValues={{
        payerEmail: '',
        payerName: '',
        message: '',
      }}
      validationSchema={assignedPaymentSchema}
      onSubmit={(values) => {
        console.log(values);
      }}
    >
      {({ isValid }) => {
        return (
          <StripeElements clientSecret={createPaymentMethodSetupState.data}>
            {/* @ts-expect-error: wrong types */}
            <Step>
              <Step.Content className='flex-column'>
                <ToggleButtonGroup
                  className='PaymentMethodButtonGroup'
                  name='paymentType'
                >
                  {availablePayModes.map(({ mode, label }, i) => {
                    return (
                      <Button
                        key={i}
                        variant={
                          selectedPayMode === mode
                            ? 'primary'
                            : 'outline-primary'
                        }
                        checked={selectedPayMode === mode}
                        value={mode}
                        onClick={handleSelectedPayMode}
                      >
                        {text(label)}
                      </Button>
                    );
                  })}
                </ToggleButtonGroup>
                {selectedPayMode === PAY_MODE.PAYMENT_METHOD && (
                  <PaymentMethodForm />
                )}
                {selectedPayMode === PAY_MODE.ASSIGNED_PAYMENT && (
                  <AssignedPaymentForm />
                )}
              </Step.Content>
            </Step>
            <StepFooter
              submit
              onCancel={redirectToMap}
              nextButtonProps={{
                label: nextLabel ?? text('continue'),
                disabled:
                  selectedPayMode === PAY_MODE.PAYMENT_METHOD
                    ? !paymentMethodId
                    : selectedPayMode === PAY_MODE.ASSIGNED_PAYMENT
                      ? !isValid
                      : false,
              }}
            />
          </StripeElements>
        );
      }}
    </Formik>
  );
};

export default PaymentMethodStep;
